import { zodResolver } from "@hookform/resolvers/zod";
import { type ChangePasswordPageValues, toChangePasswordBody } from "@seagames/contracts";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
	CardHeading,
	ErrorNotice,
	PasswordToggle,
	SubmitButton,
	TextField,
	fieldIconClass,
	fieldInputClass,
} from "../components/auth/auth-form";
import { KeyGlyph } from "../components/auth/auth-icons";
import { AuthCard, AuthSplit } from "../components/auth/auth-split";
import { ApiError } from "../lib/api/client";
import { changePassword, meQueryOptions } from "../lib/auth/session";
import { hasStoredSession } from "../lib/auth/token-store";
import { useChangePasswordFormSchema } from "../lib/i18n/use-form-schemas";

/**
 * Deliberately NOT under `/_app`.
 *
 * `must_change_password` is enforced by the server: `requireAuth` answers 403
 * PASSWORD_CHANGE_REQUIRED everywhere except /auth/me, /auth/change-password
 * and /auth/logout. Nesting this page inside the authed shell would put it
 * behind a guard that redirects here — a loop — and the shell's own data
 * fetches would all 403 anyway.
 *
 * Same split shell and card as sign-in (AuthSplit / auth-form.tsx).
 */
export const Route = createFileRoute("/change-password")({
	beforeLoad: async ({ context, location }) => {
		if (!hasStoredSession()) {
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}
		const session = await context.queryClient.ensureQueryData(meQueryOptions);
		return { session };
	},
	component: ChangePasswordPage,
});

function ChangePasswordPage() {
	const { t } = useTranslation("auth");
	const { session } = Route.useRouteContext();
	const navigate = useNavigate();
	const currentId = useId();
	const nextId = useId();
	const confirmId = useId();
	const [error, setError] = useState<unknown>(null);
	const [showCurrent, setShowCurrent] = useState(false);
	const [showNext, setShowNext] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	/**
	 * The forced first change does not ask for the temporary password again —
	 * they just signed in with it. The SERVER decides that from the token
	 * (`pwc`); this only decides which boxes to draw.
	 */
	const forced = session.must_change_password;
	const changePasswordSchema = useChangePasswordFormSchema(forced);

	const {
		register,
		handleSubmit,
		setError: setFieldError,
		formState: { errors, isSubmitting },
	} = useForm<ChangePasswordPageValues>({ resolver: zodResolver(changePasswordSchema) });

	const onSubmit = handleSubmit(async (values) => {
		setError(null);
		try {
			await changePassword(toChangePasswordBody(values, forced));
			void navigate({ to: "/", replace: true });
		} catch (err) {
			// The limiter counts failures on the CURRENT password, so a wrong one
			// belongs on that field, not in a banner the user has to hunt through.
			if (err instanceof ApiError && err.code === "INVALID_CREDENTIALS") {
				setFieldError("current_password", { message: t("changePassword.currentPassword.wrong") });
				return;
			}
			// The forced change refuses keeping the temporary password.
			if (err instanceof ApiError && err.code === "VALIDATION_FAILED" && err.fieldErrors["new_password"]) {
				setFieldError("new_password", {
					message: forced ? t("changePassword.newPassword.sameAsTemporary") : err.fieldErrors["new_password"][0],
				});
				return;
			}
			setError(err);
		}
	});

	return (
		<AuthSplit>
			<AuthCard>
				<CardHeading title={forced ? t("changePassword.titleForced") : t("changePassword.title")}>
					{forced ? t("changePassword.subtitleForced") : t("changePassword.subtitle")}
				</CardHeading>

				<ErrorNotice error={error} />

				<form onSubmit={onSubmit} noValidate className="grid gap-[1rem]">
					{!forced && (
						<TextField
							label={t("changePassword.currentPassword.label")}
							htmlFor={currentId}
							error={errors.current_password?.message}
							icon={<KeyGlyph className={fieldIconClass} />}
							trailing={
								<PasswordToggle shown={showCurrent} onToggle={() => setShowCurrent((v) => !v)} controls={currentId} />
							}
						>
							<input
								{...register("current_password")}
								id={currentId}
								type={showCurrent ? "text" : "password"}
								autoComplete="current-password"
								autoFocus
								aria-invalid={errors.current_password ? "true" : undefined}
								className={fieldInputClass}
							/>
						</TextField>
					)}

					<TextField
						label={t("changePassword.newPassword.label")}
						htmlFor={nextId}
						error={errors.new_password?.message}
						hint={t("changePassword.newPassword.hint")}
						icon={<KeyGlyph className={fieldIconClass} />}
						trailing={<PasswordToggle shown={showNext} onToggle={() => setShowNext((v) => !v)} controls={nextId} />}
					>
						<input
							{...register("new_password")}
							id={nextId}
							type={showNext ? "text" : "password"}
							autoComplete="new-password"
							autoFocus={forced}
							aria-invalid={errors.new_password ? "true" : undefined}
							className={fieldInputClass}
						/>
					</TextField>

					<TextField
						label={t("changePassword.confirmPassword.label")}
						htmlFor={confirmId}
						error={errors.confirm_password?.message}
						icon={<KeyGlyph className={fieldIconClass} />}
						trailing={
							<PasswordToggle shown={showConfirm} onToggle={() => setShowConfirm((v) => !v)} controls={confirmId} />
						}
					>
						<input
							{...register("confirm_password")}
							id={confirmId}
							type={showConfirm ? "text" : "password"}
							autoComplete="new-password"
							aria-invalid={errors.confirm_password ? "true" : undefined}
							className={fieldInputClass}
						/>
					</TextField>

					<SubmitButton pending={isSubmitting}>{t("changePassword.submit")}</SubmitButton>
				</form>
			</AuthCard>
		</AuthSplit>
	);
}
